// src/config/constants.js
import { PRODUCTION_API_URL } from './production-config';
import { logger } from '../utils/logger';

// Default API URLs for development
// Use relative path for both development and production
// This works because the frontend and backend are on the same domain
const DEFAULT_API_URL = '/api';
const DEFAULT_FRONTEND_URL = 'http://localhost:3000';

// Placeholder refresh implementation for consistency
let _refreshApiEndpointsImpl = null;
export const refreshEndpoints = () => {
  if (_refreshApiEndpointsImpl) {
    return _refreshApiEndpointsImpl();
  }
  logger.warn('API endpoint refresh requested, but function not yet initialized');
  return null;
};

/**
 * API URL Configuration
 * In production, use the PRODUCTION_API_URL from production-config.js
 * In development, use the REACT_APP_API_URL from .env, or the detected URL, or fallback to DEFAULT_API_URL
 */
export const API_URL = (process.env.NODE_ENV === 'production' 
  ? PRODUCTION_API_URL 
  : (process.env.REACT_APP_API_URL || DEFAULT_API_URL)).trim();

// Function to get the most up-to-date API URL
export const getApiUrl = () => {
  if (process.env.NODE_ENV === 'production') {
    return PRODUCTION_API_URL;
  }
  return process.env.REACT_APP_API_URL || DEFAULT_API_URL;
};

/**
 * Frontend URL for links in emails and redirects
 */
export const FRONTEND_URL = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_URL || window.location.origin)
  : (process.env.REACT_APP_URL || DEFAULT_FRONTEND_URL);

// API Configuration object
export const API_CONFIG = {
  BASE_URL: API_URL
};

// Employer route prefixes for route detection
export const EMPLOYER_ROUTE_PREFIXES = [
  '/dashboard',
  '/employer',
  '/inbox',
  '/applications',
  '/database',
  '/posted-jobs',
  '/post-job',
  '/analytics',
  '/branding',
  '/billing',
  '/support',
  '/notifications',
  '/profile',
  '/recruiters',
  '/recruiter-performance'
];

// Helper function to check if pathname is an employer route
export const isEmployerRoute = (pathname) => {
  return EMPLOYER_ROUTE_PREFIXES.some(prefix => pathname.startsWith(prefix));
};

// Log API URL in development to help with debugging
logger.debug(`🔌 API URL: ${API_URL}`);
logger.debug(`🌐 Frontend URL: ${FRONTEND_URL}`);
// Debug any potential spaces or special characters in the URL
logger.debug('API URL character codes:', [...API_URL].map(c => c.charCodeAt(0)));

/**
 * Authentication Configuration
 */
export const AUTH_CONFIG = {
  // Storage keys
  TOKEN_KEY: 'authToken',
  USER_KEY: 'userData',
  
  // Token expiration
  TOKEN_EXPIRY_BUFFER: 5 * 60 * 1000, // 5 minutes in milliseconds
  
  // Authentication methods
  AUTH_METHODS: {
    PHONE_OTP: 'phone_otp',
    // EMAIL_OTP removed to prevent duplicate OTPs
    EMAIL_PASSWORD: 'email_password'},
  
  // OTP configuration
  OTP: {
    EXPIRY_SECONDS: 10 * 60, // 10 minutes
    LENGTH: 6,
    RESEND_DELAY: 30, // seconds
  },
  
  // Endpoints (without leading slash - baseURL already includes /api)
  ENDPOINTS: {
    LOGIN: 'auth/login',
    SIGNUP: 'auth/signup',
    SEND_OTP: 'auth/send-otp',
    SEND_EMAIL_OTP: 'auth/send-email-otp',
    VERIFY_OTP: 'auth/verify-otp',
    VERIFY_EMAIL_OTP: 'auth/verify-email-otp',
    VERIFY_EMAIL: 'auth/verify-email',
    RESEND_VERIFICATION: 'auth/resend-verification',
    FORGOT_PASSWORD: 'auth/forgot-password',
    RESET_PASSWORD: 'auth/reset-password',
    CHANGE_PASSWORD: 'auth/change-password',
    LOGOUT: 'auth/logout',
    ME: 'auth/me',
    COMPLETE_PROFILE: 'auth/complete-profile'},
  
  // Authentication debugging
  DEBUG: {
    ENABLED: process.env.NODE_ENV !== 'production',
    LOG_AUTH_STATE: true,
    LOG_TOKEN_VALIDATION: true,
    LOG_NAVIGATION: true},
  
  // Default user object fields
  DEFAULT_USER: {
    profileCompleted: false,
    isPhoneVerified: false,
    userType: null, // Must be explicitly set
  },
  
  // Authentication state validation
  VALIDATION: {
    REQUIRED_USER_FIELDS: ['id', 'userType'],
    TOKEN_GRACE_PERIOD: 5 * 60 * 1000, // 5 minutes in milliseconds
    REPAIR_CORRUPTED_DATA: true},
  
  // Authentication flow timeouts
  TIMEOUTS: {
    AUTH_STATE_SETTLE: 200, // ms to wait for auth state to settle before redirecting (reduced from 500)
    NAVIGATION_DELAY: 100, // ms to wait before navigation after auth state change
    TOKEN_REFRESH: 60 * 1000, // 1 minute
  },
  
  // Authentication retry logic
  RETRY: {
    MAX_ATTEMPTS: 3,
    BACKOFF_FACTOR: 1.5, // Each retry waits 1.5x longer than the previous
    INITIAL_DELAY: 1000, // 1 second
  }};

/**
 * Validation Configuration
 */
export const VALIDATION = {
  // Phone validation
  PHONE: {
    MIN_LENGTH: 8,
    MAX_LENGTH: 15,
    EXAMPLE: '+1 (555) 123-4567'},
  
  // Password validation
  PASSWORD: {
    MIN_LENGTH: 8,
    PATTERN: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8}$/,
    REQUIREMENTS: 'Password must be at least 8 characters and include uppercase, lowercase, and numbers'},
  
  // Email validation
  EMAIL: {
    PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    EXAMPLE: 'user@example.com'}};

/**
 * Validates that all required environment variables are set.
 * Call this function during app bootstrap (e.g., in index.js)
 * @returns {boolean} True if all required variables are set
 * @throws {Error} In production, throws if required variables are missing
 */
export const validateConfig = () => {
  const missingVars = [];
  const warnings = [];
  
  // In production, we use PRODUCTION_API_URL from production-config.js
  // In development, check for REACT_APP_API_URL
  if (process.env.NODE_ENV !== 'production') {
    if (!process.env.REACT_APP_API_URL) {
      warnings.push(`REACT_APP_API_URL not set, using default: ${DEFAULT_API_URL}`);
    }
    
    // In development, provide warnings but don't throw
    if (warnings.length > 0) {
      console.warn('[INFO] Environment Configuration:');
      warnings.forEach(warning => console.warn(`  - ${warning}`));
      console.warn('To customize, create a .env file in the project root.');
      console.warn('See .env.example for a template.');
    }
  }
  
  return true;
};

/**
 * Error Messages
 */
export const ERROR_MESSAGES = {
  DEFAULT: 'Something went wrong. Please try again later.',
  NETWORK: 'Network error. Please check your internet connection.',
  UNAUTHORIZED: 'You are not authorized to access this resource.',
  INVALID_CREDENTIALS: 'Invalid credentials. Please try again.',
  INVALID_OTP: 'Invalid verification code. Please try again.',
  EXPIRED_OTP: 'Verification code has expired. Please request a new one.',
  RATE_LIMITED: 'Too many attempts. Please try again later.',
  INVALID_PHONE: 'Please enter a valid phone number.',
  INVALID_EMAIL: 'Please enter a valid email address.',
  INVALID_PASSWORD: 'Password does not meet the requirements.',
  EMAIL_NOT_VERIFIED: 'Please verify your email address before logging in.',
  ACCOUNT_LOCKED: 'Your account has been temporarily locked due to too many failed attempts.',
  
  // Authentication specific errors
  AUTH: {
    TOKEN_EXPIRED: 'Your session has expired. Please log in again.',
    TOKEN_INVALID: 'Invalid authentication token. Please log in again.',
    TOKEN_MISSING: 'Authentication token not found. Please log in.',
    USER_TYPE_MISSING: 'User type could not be determined. Please log in again.',
    USER_TYPE_MISMATCH: 'You are not authorized to access this page with your current user type.',
    PROFILE_INCOMPLETE: 'Please complete your profile to continue.',
    CANDIDATE_AUTH_FAILED: 'Candidate authentication failed. Please try again.',
    DATA_INTEGRITY: 'Your authentication data appears to be corrupted. Please log in again.'}
};

/**
 * Company Information
 */
export const COMPANY_INFO = {
  NAME: 'GenZ Portal',
  SUPPORT_EMAIL: 'support@genzportal.com',
  SUPPORT_PHONE: '+1 (555) 123-4567',
  WEBSITE: 'https://genzportal.com'};

/**
 * Local Storage Keys
 */
export const STORAGE_KEYS = {
  TOKEN: AUTH_CONFIG.TOKEN_KEY,
  USER: AUTH_CONFIG.USER_KEY,
  THEME: 'theme',
  LANGUAGE: 'language',
  LAST_ROUTE: 'lastRoute',
  AUTH_STATE: 'authState', // For tracking authentication state transitions
  AUTH_ERRORS: 'authErrors', // For tracking authentication errors
  USER_TYPE: 'userType', // Legacy key for backward compatibility
};

/**
 * API Endpoints
 * These are relative paths that will be combined with the API_URL base by axios.
 * DO NOT include /api prefix here - it's already in the baseURL
 */
export const API_ENDPOINTS = {
  AUTH: AUTH_CONFIG.ENDPOINTS,
  CANDIDATES: {
    LIST: 'candidates',
    DETAILS: (id) => `candidates/${id}`,
    SEARCH: 'candidates/search'},
  JOBS: {
    LIST: 'jobs',
    DETAILS: (id) => `jobs/${id}`,
    CREATE: 'jobs',
    UPDATE: (id) => `jobs/${id}`,
    DELETE: (id) => `jobs/${id}`},
  // Dashboard endpoint
  DASHBOARD: 'dashboard'};

// Make API_ENDPOINTS and utility functions globally accessible for debugging
if (typeof window !== 'undefined') {
  window.API_ENDPOINTS = API_ENDPOINTS;
  window.getApiUrl = getApiUrl;

  // Debug logging for API URL configuration
  console.log('API Configuration:', {
    baseUrl: API_CONFIG.BASE_URL,
    sampleEndpoints: {
      SEND_OTP: API_ENDPOINTS.AUTH.SEND_OTP,
      VERIFY_OTP: API_ENDPOINTS.AUTH.VERIFY_OTP
    }
  });
}

/**
 * Export for backward compatibility
 */
export const COMPANY_NAME = COMPANY_INFO.NAME;
export const TOKEN_KEY = STORAGE_KEYS.TOKEN;
export const DEFAULT_ERROR_MESSAGE = ERROR_MESSAGES.DEFAULT;
